import React, { useEffect, useState } from 'react';
import { AutoComplete, Checkbox, Form, Input, Select } from 'antd';
import { StyledButton } from 'component/button_components/button_general/ButtonGeneralStyle';
import { MiniInputWrap } from 'pages/settings_page/style';
import { FormHeader, FormItem, ModalFormContainer, RowWrapper } from 'component/form_component/style';
import { useDispatch, useSelector } from 'react-redux';
import { getChildOuDropdown, getDomainContainers } from 'redux/slices/getChildOu';
import { getGroupsExceptUser, getGroupsOfUser } from 'redux/slices/usersGroupSlice';
import TransferDataComponent from 'component/form_component/transferDataBox';
import { getUpnSuggestions } from 'redux/slices/userSlice';
import { bulkUserCreate } from 'redux/slices/createUser';
import { Link } from 'react-router-dom';
import { autoGeneratePassword } from 'redux/slices/resetPassword';
import { SweetSweetAlert } from 'component/sweetalert_components';
import ModalOpenLoader from 'component/loader_component/modalOpenLoader';
import { useRefreshUserTab } from 'component/table_component/users_data/refreshUserData';
import { InputPatternRuleAD } from 'utils/input_pattern';
import Swal from 'sweetalert2';

const BulkCreateUsers = ({ setBulkCreateModal }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [samName, setSamName] = useState('');
  const [upnValue, setUpnValue] = useState('');
  const [splitter, setSplitter] = useState('');
  const [loading, setLoading] = useState(false);
  const refreshUserTab = useRefreshUserTab();
  const [suffixStart, setSuffixStart] = useState(1);
  const [removedGroups, setRemovedGroups] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [passwordAtNextLogon, setPasswordAtNextLogon] = useState(true);
  const selectedNode = useSelector((state) => state.selectedNode);
  const adminOuName = useSelector((state) => state.ouTreeData.data);
  const { upnSuggestions } = useSelector((state) => state.getUsers);
  const { groupsExceptUser, groupsOfUser } = useSelector((state) => state.usersGroupSlice);
  const { childOuDropdown, domainContainers } = useSelector((state) => state.getChildOuData);

  useEffect(() => {
    dispatch(getUpnSuggestions(selectedNode.domainName ?? localStorage.getItem('domain')));
  }, []);

  useEffect(() => {
    let initialOuName = '';
    let fqdn = selectedNode.domainName ?? localStorage.getItem('domain');
    if (localStorage.getItem('role') === 'ouadmin') {
      initialOuName = adminOuName.dnName;
    } else {
      initialOuName =
        selectedNode.selectedNode && selectedNode.domainDn ? selectedNode.domainDn : localStorage.getItem('domainDn');
    }
    dispatch(
      getChildOuDropdown({
        fqdn: fqdn,
        ouName: initialOuName,
      })
    );
    dispatch(getDomainContainers(fqdn));
    dispatch(
      getGroupsExceptUser({
        fqdn: fqdn,
        userName: '',
        ouName: initialOuName,
      })
    );
    dispatch(
      getGroupsOfUser({
        fqdn: fqdn,
        userName: '',
      })
    );
  }, [selectedNode]);

  useEffect(() => {
    if (selectedNode.selectedNode) {
      form.setFieldsValue({
        ouName: selectedNode.typeOfData === 'domain' ? null : selectedNode.selectedNode.dnName,
      });
    }
  }, [selectedNode.selectedNode]);

  useEffect(() => {
    if (upnSuggestions.length) {
      setUpnValue(upnSuggestions[0]);
    }
  }, [upnSuggestions]);

  let mergedArray = [];
  let indexCounter = 1;
  mergedArray = [
    ...groupsOfUser.map((group) => ({
      title: group.groupName,
      dn: group.distinguishedName,
      groupAvailability: true,
      key: String(indexCounter++),
      primaryGroup: group.primaryGroup,
    })),
    ...groupsExceptUser.map((group) => ({
      title: group.groupName,
      dn: group.distinguishedName,
      groupAvailability: false,
      key: String(indexCounter++),
    })),
  ];

  const keysWithTrueGroupAvailability =
    mergedArray && mergedArray.filter((item) => item.groupAvailability).map((item) => item.key);

  const handleTransferChange = (addedData, _, removedData) => {
    setAssignedGroups(addedData);
    setRemovedGroups(removedData);
  };

  const generatePassword = async () => {
    const passwordValue = await dispatch(autoGeneratePassword(selectedNode.currentdomainId));
    if (passwordValue?.payload.status === 200) {
      return passwordValue.payload.data;
    }
  };
  const handleGeneratePassword = async () => {
    const newPassword = await generatePassword();
    form.setFieldsValue({
      password: newPassword,
    });
  };

  const onFinish = async (values) => {
    const addGroupNames = [];
    assignedGroups.forEach((item) => {
      if (!removedGroups.includes(item)) {
        addGroupNames.push(item.dn);
      }
    });
    let splitterInput = splitter;
    if (splitterInput.match(InputPatternRuleAD.pattern)) {
      const result = await Swal.fire({
        icon: 'info',
        html: `The Splitter value ${splitterInput} ${InputPatternRuleAD.message}`,
        showConfirmButton: true,
        confirmButtonColor: 'var(--bg-button-default-color)',
        showCancelButton: true,
        cancelButtonColor: 'green',
        width: '500px',
        heightAuto: false,
        reverseButtons: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        customClass: {
          icon: 'custom-swal-icon',
          title: 'custom-swal-title',
        },
      });
      if (result.isConfirmed) {
        splitterInput = splitterInput.replace(InputPatternRuleAD.pattern, '_');
        setSplitter(splitterInput);
      } else {
        return;
      }
    }
    values = {
      bulkUser: {
        samAccountName: values.samName,
        ouName:
          selectedNode.typeOfData !== 'ou' && selectedNode.typeOfData !== 'domain'
            ? selectedNode.selectedNode.dnName
            : values.ouName,
        fqdn: selectedNode.domainName ?? localStorage.getItem('domain'),
        splitter: splitterInput,
        password: values.password,
        suffixStart: parseInt(values.suffixStart),
        suffixEnd: parseInt(values.suffixEnd),
        upnSuffix: upnValue,
        passwordAtNextLogon: passwordAtNextLogon,
      },
      addGroupNames,
    };

    try {
      setLoading(true);
      const response = await dispatch(bulkUserCreate(values));
      if (response?.payload.status === 200) {
        await SweetSweetAlert({ sweetData: response.payload.data, icon: 'success', buttonColor: 'green' });
        await refreshUserTab();
      } else if (response?.payload.status === 400) {
        await SweetSweetAlert({ text: response.payload.data, icon: 'error', buttonColor: 'red' });
      } else {
        SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      }
    } catch (error) {
      SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
    } finally {
      setLoading(false);
      setBulkCreateModal(false);
    }
  };

  const transformedSuggestions = upnSuggestions.map((item) => ({
    value: item,
  }));

  return (
    <ModalFormContainer className='af-bulk-createuser-modal-wrap'>
      {loading && <ModalOpenLoader />}
      <FormHeader className='af-bulk-createuser-header'>Create Users</FormHeader>
      <div className='af-bulk-createuser-wrap'>
        <Form
          size='large'
          name='bulkcreate'
          layout='vertical'
          onFinish={onFinish}
          form={form}
          className='af-bulk-createuser-form-element'
          initialValues={{ suffixStart: 1, suffixEnd: 10 }}
        >
          <RowWrapper className='af-bulk-createuser-form-items-wrap-1'>
            <FormItem
              className='af-bulk-createuser-form-item-samName'
              label='Sam Name Base'
              name='samName'
              rules={[
                {
                  required: true,
                  message: `Please Enter the SAM Account Name`,
                },
              ]}
            >
              <Input
                className='af-bulk-createuser-input-samName'
                onChange={(e) => {
                  setSamName(e.target.value);
                }}
              />
            </FormItem>
            <MiniInputWrap className='af-bulk-createuser-mini-input-wrap'>
              <FormItem
                className='af-bulk-createuser-form-item-splitter'
                label='Splitter'
                name='splitter'
                onChange={(e) => setSplitter(e.target.value)}
              >
                <Input className='af-bulk-createuser-input-splitter' />
              </FormItem>
              <FormItem
                className='af-bulk-createuser-form-item-suffix-start'
                label='Suffix Start'
                name='suffixStart'
                onChange={(e) => {
                  setSuffixStart(e.target.value);
                }}
                rules={[
                  {
                    required: true,
                    message: `Please Enter the Suffix Start`,
                  },
                ]}
                style={{ minWidth: '90px' }}
              >
                <Input type='number' className='af-bulk-createuser-input-suffix-start' />
              </FormItem>
              <FormItem
                className='af-bulk-createuser-form-item-suffix-end'
                label='Suffix End'
                name='suffixEnd'
                rules={[
                  {
                    required: true,
                    message: `Please Enter the Suffix End`,
                  },
                ]}
              >
                <Input type='number' className='af-bulk-createuser-input-suffix-end' />
              </FormItem>
            </MiniInputWrap>
          </RowWrapper>
          <RowWrapper style={{ justifyContent: 'space-between' }} className='af-bulk-createuser-form-items-wrap-2'>
            <FormItem className='af-bulk-createuser-form-item-upn' label='UPN Name' name='upn'>
              <div
                style={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}
                className='af-bulk-createuser-select-upn-wrap'
              >
                <div
                  style={{ marginRight: '5px', maxWidth: '228px', width: 'auto' }}
                  className='af-bulk-createuser-select-upn-input'
                >
                  {samName.trim()}
                  {splitter.trim()}
                  {suffixStart}@
                </div>

                <AutoComplete
                  value={upnValue}
                  options={transformedSuggestions}
                  onChange={(value) => {
                    setUpnValue(value);
                  }}
                  onSelect={(value) => {
                    setUpnValue(value);
                  }}
                />
              </div>
            </FormItem>
            <FormItem style={{ margin: '0px' }}>
              <FormItem
                className='af-bulk-createuser-form-item-password'
                label='Password'
                name='password'
                rules={[
                  {
                    required: true,
                    message: `Please Enter the Password`,
                  },
                ]}
                style={{ marginBottom: '5px' }}
              >
                <Input.Password className='af-bulk-createuser-input-password' />
              </FormItem>
              <Link onClick={handleGeneratePassword} className='af-bulk-createuser-password-generate'>
                Generate
              </Link>
            </FormItem>
          </RowWrapper>
          <RowWrapper className='af-bulk-createuser-form-items-wrap-3'>
            <FormItem className='oddFormItem af-bulk-createuser-form-item-ou' label='Ou' name='ouName'>
              <Select allowClear showSearch placeholder='Select a Ou' className='af-bulk-createuser-selector-ou'>
                {domainContainers
                  .filter(
                    (container) => typeof container === 'object' && container.distinguishedName.includes('CN=Users')
                  )
                  .map((container) => (
                    <Select.Option
                      className='af-bulk-createuser-select-option-ou'
                      key={container.distinguishedName}
                      value={container.distinguishedName}
                    >
                      {container.name} (Container)
                    </Select.Option>
                  ))}
                {localStorage.getItem('role') === 'ouadmin' && (
                  <Select.Option
                    className='af-bulk-createuser-select-option-ou'
                    key={adminOuName.dnName}
                    value={adminOuName.dnName}
                  >
                    {adminOuName.name}
                  </Select.Option>
                )}
                {childOuDropdown.map((ou) => (
                  <Select.Option className='af-bulk-createuser-select-option-ou' key={ou.dnName} value={ou.dnName}>
                    {ou.name}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>
          </RowWrapper>
          <FormItem className='af-bulk-createuser-form-item-AssignGroup' label='Assign Group' name='assign'>
            <TransferDataComponent
              className='af-bulk-createuser-transferbox'
              leftBoxData={mergedArray}
              rightBoxData={keysWithTrueGroupAvailability}
              onChange={handleTransferChange}
              leftBoxTitle={'Available Group(s)'}
              rightBoxTitle={'Assigned Group(s)'}
            />
          </FormItem>
          <FormItem
            className='hidden_label display-none af-bulk-createuser-form-item-nextLogon'
            label='nextLogon'
            name='nextLogon'
          >
            <Checkbox
              checked={passwordAtNextLogon}
              onChange={(e) => setPasswordAtNextLogon(e.target.checked)}
              className='af-bulk-createuser-nextlogon-checkbox'
            >
              Force user to change password on next login.
            </Checkbox>
          </FormItem>

          <FormItem style={{ textAlign: 'end', marginBottom: 0 }} className='af-bulk-createuser-form-item-button'>
            <StyledButton type='primary' htmlType='submit' className='af-bulk-createuser-submit-button'>
              Submit
            </StyledButton>
          </FormItem>
        </Form>
      </div>
    </ModalFormContainer>
  );
};

export default BulkCreateUsers;
